export type SoundType = 'move' | 'rotate' | 'lock' | 'clear' | 'start' | 'over';

interface SoundConfig {
  file: string;
  volume: number;
}

interface SafariWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const baseUrl = import.meta.env.BASE_URL;
const soundConfig: Record<SoundType, SoundConfig> = {
  move: { file: 'move.wav', volume: 0.48 },
  rotate: { file: 'rotate.wav', volume: 0.82 },
  lock: { file: 'lock.wav', volume: 0.92 },
  clear: { file: 'clear.wav', volume: 1 },
  start: { file: 'start.wav', volume: 0.9 },
  over: { file: 'over.wav', volume: 0.9 },
};

export class SoundPlayer {
  enabled: boolean;
  private context: AudioContext | undefined;
  private readonly buffers = new Map<SoundType, AudioBuffer>();
  private readonly loads = new Map<SoundType, Promise<AudioBuffer | undefined>>();
  private readonly fallbacks = new Map<SoundType, HTMLAudioElement[]>();
  private readonly fallbackIndexes = new Map<SoundType, number>();

  constructor(enabled: boolean) {
    this.enabled = enabled;
    this.prepare();
  }

  async unlock(): Promise<void> {
    const context = this.ensureContext();
    if (!context) return;
    if (context.state === 'suspended') {
      try {
        await context.resume();
      } catch {
        return;
      }
    }
    if (context.state === 'running') {
      const silent = context.createBufferSource();
      silent.buffer = context.createBuffer(1, 1, context.sampleRate);
      silent.connect(context.destination);
      silent.start();
    }
  }

  play(type: SoundType): void {
    if (!this.enabled) return;
    const context = this.ensureContext();
    const buffer = this.buffers.get(type);
    if (context?.state === 'running' && buffer) {
      this.playBuffer(context, buffer, soundConfig[type].volume);
      return;
    }

    void this.unlock();
    this.playFallback(type);
  }

  private playBuffer(context: AudioContext, buffer: AudioBuffer, volume: number): void {
    const source = context.createBufferSource();
    const gain = context.createGain();
    gain.gain.value = volume;
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(context.destination);
    source.start();
  }

  private prepare(): void {
    const context = this.ensureContext();
    for (const type of Object.keys(soundConfig) as SoundType[]) {
      this.createFallbackPool(type);
      if (context) this.load(type, context);
    }
  }

  private ensureContext(): AudioContext | undefined {
    if (this.context) return this.context;
    const AudioContextClass = window.AudioContext ?? (window as SafariWindow).webkitAudioContext;
    if (!AudioContextClass) return undefined;
    try {
      this.context = new AudioContextClass();
      return this.context;
    } catch {
      return undefined;
    }
  }

  private load(type: SoundType, context: AudioContext): void {
    if (this.loads.has(type)) return;
    const promise = fetch(`${baseUrl}sounds/${soundConfig[type].file}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Audio request failed: ${response.status}`);
        return response.arrayBuffer();
      })
      .then((data) => context.decodeAudioData(data))
      .then((buffer) => {
        this.buffers.set(type, buffer);
        return buffer;
      })
      .catch(() => undefined);
    this.loads.set(type, promise);
  }

  private createFallbackPool(type: SoundType): void {
    const pool = Array.from({ length: 3 }, () => {
      const audio = new Audio(`${baseUrl}sounds/${soundConfig[type].file}`);
      audio.preload = 'auto';
      audio.volume = soundConfig[type].volume;
      return audio;
    });
    this.fallbacks.set(type, pool);
    this.fallbackIndexes.set(type, 0);
  }

  private playFallback(type: SoundType): void {
    const pool = this.fallbacks.get(type);
    if (!pool?.length) return;
    const index = this.fallbackIndexes.get(type) ?? 0;
    const audio = pool[index];
    this.fallbackIndexes.set(type, (index + 1) % pool.length);
    audio.currentTime = 0;
    void audio.play().catch(() => undefined);
  }
}
